// src/apiSession.js
import { auth } from './components/FirebaseConfig';

// Where the API lives.
//
// The fallback is the real production backend, not a placeholder: if
// REACT_APP_API_BASE is ever missing from a deploy's environment, the app
// should still reach a working server rather than silently calling a dead
// host. That mattered here — the previous fallback pointed at a Render
// service that had been serving three-month-old code and 500ing on every
// database route.
//
// Architecture (2026-08-18): browser -> this backend, directly. The
// Cloudflare Worker that used to sit in between is gone; it added a hop, an
// extra thing to deploy, and its own failure mode, while the backend it
// fronted was the actual problem.
export const API_BASE =
  window.location.hostname === 'localhost'
    ? 'http://localhost:5002'
    : (process.env.REACT_APP_API_BASE || 'https://api.assistalien.com');

/**
 * fetch with a deadline. The browser's own network timeout is minutes; a
 * server that accepts the connection and never answers — the signature of a
 * box in swap — held every screen open for that long. 20 seconds is longer
 * than any healthy request here and short enough to show a message instead
 * of a blank page.
 */
export const DEFAULT_TIMEOUT_MS = 20_000;
export async function fetchWithTimeout(url, opts = {}, ms = DEFAULT_TIMEOUT_MS) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), ms);
  try {
    return await fetch(url, { ...opts, signal: opts.signal || ctrl.signal });
  } catch (err) {
    if (err?.name === 'AbortError') {
      const e = new Error('The server took too long to respond. Try again in a moment.');
      e.status = 0; e.timeout = true;
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export async function fetchJson(path, opts = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const forceFresh = !!opts.forceFreshToken;
  const user = auth.currentUser;
  let token = null;

  try {
    if (user) token = await user.getIdToken(forceFresh);
  } catch (e) {
    console.warn('fetchJson token fetch failed', e?.message || e);
  }

  const headers = {
    'Content-Type': 'application/json',
    ...(opts.headers || {}),
  };

  if (!headers['Authorization'] && token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const fetchOpts = {
    credentials: 'include',
    cache: opts.cache || 'no-store',
    ...opts,
    headers,
  };

  const res = await fetchWithTimeout(url, fetchOpts);

  if (res.status === 304) {
    const retryHeaders = { ...headers };
    if (user) {
      try {
        const fresh = await user.getIdToken(true);
        retryHeaders['Authorization'] = `Bearer ${fresh}`;
      } catch {}
    }
    const retryRes = await fetch(url, {
      ...fetchOpts,
      cache: 'no-store',
      headers: retryHeaders,
    });
    return handleFetchResponse(retryRes);
  }

  return handleFetchResponse(res);
}

async function handleFetchResponse(res) {
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {}
  if (!res.ok) {
    let msg = data && (data.error || data.message);
    if (!msg) {
      msg = text && text.trim().startsWith('<') 
        ? `Backend error (${res.status}): The server is not responding right now. Try again in a moment.` 
        : (text || `HTTP ${res.status}`);
    }
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function sessionLogin() {
  const user = auth.currentUser;
  if (!user) return;
  const token = await user.getIdToken(true);
  await fetchJson('/api/sessionLogin', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function sessionLogout(opts = {}) {
  await fetchJson('/api/sessionLogout', { method: 'POST' });
  if (opts.signOutFirebase) {
    try {
      await auth.signOut();
    } catch {}
  }
}

/** Fetch binary/Blob endpoints */
export async function fetchBlob(path, opts = {}) {
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const user = auth.currentUser;
  let token = null;
  try {
    if (user) token = await user.getIdToken(false);
  } catch {}
  const res = await fetch(url, {
    credentials: 'include',
    ...opts,
    headers: {
      ...(token ? { 'X-ID-Token': token } : {}),
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    const err = new Error(text || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.blob();
}

export default {
  API_BASE,
  fetchJson,
  sessionLogin,
  sessionLogout,
  fetchBlob,
};
